import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Music2,
  Plus,
  RefreshCcw,
  Send,
  Trash2,
  User,
} from 'lucide-react';
import { Badge, Modal } from '../Scheduler/utils';
import PhotoAssetBrowser from '../CreativeStudio/PhotoAssetBrowser';
import GigReviewPanel from './GigReviewPanel';
import {
  artistToPerformerFormPatch,
  buildGigInvitePayload,
  buildGigPayload,
  buildSubmissionUrl,
  cancelGig,
  createEmptyGigForm,
  createGig,
  deleteArtist,
  getGig,
  gigToFormState,
  listArtists,
  publishGig,
  replaceGigPerformer,
  requestGigChanges,
  sendGigInvite,
  MAX_GIG_HIGHLIGHT_PHOTOS,
  updateGig,
  uploadGigHighlightPhotos,
} from './gigApi';
import { buildGigCampaignRequest, openGigCampaignInCreativeStudio } from './gigCampaignBridge';
import { formatGigDisplayStatusLabel, getGigDisplayStatus, isGigCompleted } from './gigStatus';
import { PREFERRED_CONTACT_METHOD_OPTIONS } from '../../utils/gigProfileFields';

function formatRangeSummary(form) {
  const start = form?.startsAtLocal ? new Date(form.startsAtLocal) : null;
  const end = form?.endsAtLocal ? new Date(form.endsAtLocal) : null;
  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
    return 'Choose a valid start and end time';
  }
  return `${start.toLocaleString()} – ${end.toLocaleString()}`;
}

function isHistoricalPerformer(performer) {
  return ['replaced', 'cancelled'].includes(String(performer?.status || '').toLowerCase());
}

function createBlankPerformer(siteId) {
  return createEmptyGigForm({ siteId }).performers[0];
}

function performerLabel(performer, fallbackIndex) {
  return performer?.displayName || `Performer ${fallbackIndex + 1}`;
}

function parseLocalDateTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatLocalDateTime(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const tzOffset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - tzOffset).toISOString().slice(0, 16);
}

function buildSafeGigRange(startValue, endValue, durationHours = 2) {
  const start = parseLocalDateTime(startValue);
  const end = parseLocalDateTime(endValue);
  if (!start) {
    return {
      startsAtLocal: startValue,
      endsAtLocal: endValue,
    };
  }

  const minimumEnd = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
  return {
    startsAtLocal: formatLocalDateTime(start),
    endsAtLocal: !end || end.getTime() <= start.getTime()
      ? formatLocalDateTime(minimumEnd)
      : formatLocalDateTime(end),
  };
}

function normalizeHighlightPhotoState(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.creativeId || 0) > 0)
    .map((item, index) => ({
      ...item,
      sortOrder: Number.isInteger(Number(item.sortOrder)) ? Number(item.sortOrder) : index * 10,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.creativeId || 0) - Number(b.creativeId || 0));
}

export default function GigEditorModal({
  open,
  gigId,
  siteId,
  siteName,
  defaultStart,
  defaultEnd,
  hasPreviousGig = false,
  hasNextGig = false,
  onPreviousGig,
  onNextGig,
  onClose,
  onChanged,
  onMessage,
}) {
  const [form, setForm] = useState(() => createEmptyGigForm({ siteId }));
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [artists, setArtists] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const [replacementTargetId, setReplacementTargetId] = useState(null);
  const [replacementDraft, setReplacementDraft] = useState(() => createBlankPerformer(siteId));
  const [gigDetailsExpanded, setGigDetailsExpanded] = useState(true);
  const [expandedPerformerKeys, setExpandedPerformerKeys] = useState({});
  const [uploadingHighlights, setUploadingHighlights] = useState(false);
  const [gigArtworkBrowserOpen, setGigArtworkBrowserOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function loadGig() {
      setError('');
      setReplacementTargetId(null);
      setReplacementDraft(createBlankPerformer(siteId));

      if (!gigId) {
        setLoading(false);
        setGigDetailsExpanded(true);
        setExpandedPerformerKeys({ 'new-0': true });
        setForm(
          createEmptyGigForm({
            siteId,
            startsAtUtc: defaultStart?.toISOString?.() || null,
            endsAtUtc: defaultEnd?.toISOString?.() || null,
          })
        );
        return;
      }

      try {
        setLoading(true);
        const gig = await getGig(gigId);
        if (cancelled) return;
        const nextForm = gigToFormState(gig);
        setForm(nextForm);
        setGigDetailsExpanded(false);
        setExpandedPerformerKeys({});
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load gig');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadGig();

    return () => {
      cancelled = true;
    };
  }, [defaultEnd, defaultStart, gigId, open, siteId]);

  const performers = Array.isArray(form.performers) && form.performers.length
    ? form.performers
    : [createBlankPerformer(form.siteId || siteId)];
  const highlightPhotos = normalizeHighlightPhotoState(form.highlightPhotos);
  const performerEntries = performers.map((performer, index) => ({ performer, index }));
  const activeEntries = performerEntries.filter(({ performer }) => !isHistoricalPerformer(performer));
  const historyEntries = performerEntries.filter(({ performer }) => isHistoricalPerformer(performer));
  const resolvedSiteId = Number(form.siteId || siteId) || null;
  const artistsById = useMemo(
    () => new Map(artists.map((artist) => [artist.id, artist])),
    [artists]
  );
  const replacementTarget = useMemo(() => {
    const normalizedTargetId = Number(replacementTargetId || 0);
    if (!Number.isInteger(normalizedTargetId) || normalizedTargetId <= 0) {
      return null;
    }
    return performers.find((performer) => Number(performer?.id || 0) === normalizedTargetId) || null;
  }, [performers, replacementTargetId]);
  const rangeSummary = useMemo(() => formatRangeSummary(form), [form]);
  const isExistingGig = !!form.id;
  const gigCompleted = isGigCompleted(form);
  const gigDisplayStatus = getGigDisplayStatus(form);
  const showHighlightSection = isExistingGig && (gigCompleted || highlightPhotos.length > 0);

  useEffect(() => {
    if (!open) return;
    if (!resolvedSiteId) {
      setArtists([]);
      setArtistsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadArtists() {
      try {
        setArtistsLoading(true);
        const items = await listArtists({ siteId: resolvedSiteId });
        if (cancelled) return;
        setArtists(items);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load artists');
      } finally {
        if (!cancelled) {
          setArtistsLoading(false);
        }
      }
    }

    loadArtists();

    return () => {
      cancelled = true;
    };
  }, [open, resolvedSiteId]);

  const updateForm = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const updateGigStart = (nextStartValue) => {
    setForm((current) => ({
      ...current,
      ...buildSafeGigRange(nextStartValue, null),
    }));
  };

  const updateGigEnd = (nextEndValue) => {
    setForm((current) => ({
      ...current,
      ...buildSafeGigRange(current.startsAtLocal, nextEndValue),
    }));
  };

  const updatePerformers = (updater) => {
    setForm((current) => {
      const nextPerformers = updater(Array.isArray(current.performers) ? current.performers : []);
      return {
        ...current,
        performers: nextPerformers.length ? nextPerformers : [createBlankPerformer(current.siteId || siteId)],
      };
    });
  };

  const updateHighlightPhotos = (updater) => {
    setForm((current) => {
      const nextItems = normalizeHighlightPhotoState(
        updater(Array.isArray(current.highlightPhotos) ? current.highlightPhotos : [])
      ).map((item, index) => ({
        ...item,
        sortOrder: index * 10,
      }));

      return {
        ...current,
        highlightPhotos: nextItems,
      };
    });
  };

  const updatePerformer = (index, patch) => {
    updatePerformers((current) =>
      current.map((performer, performerIndex) =>
        performerIndex === index ? { ...performer, ...patch } : performer
      )
    );
  };

  const addPerformer = () => {
    const newIndex = performers.length;
    updatePerformers((current) => [...current, createBlankPerformer(resolvedSiteId)]);
    setExpandedPerformerKeys((prev) => ({
      ...prev,
      [`new-${newIndex}`]: true,
    }));
  };

  const removePerformer = (index) => {
    updatePerformers((current) => current.filter((_, performerIndex) => performerIndex !== index));
  };

  const persistGig = async () => {
    const payload = buildGigPayload(form, { includeStatus: !form.id });
    if (!payload.siteId) {
      throw new Error('A site is required before saving gigs');
    }
    if (!payload.title) {
      throw new Error('Gig title is required');
    }
    if (!payload.startsAtUtc || !payload.endsAtUtc) {
      throw new Error('Gig start and end time are required');
    }

    const savedGig = form.id
      ? await updateGig(form.id, payload)
      : await createGig(payload);

    setForm(gigToFormState(savedGig));
    await onChanged?.(savedGig);
    return savedGig;
  };

  const runAction = async (work, successMessage) => {
    try {
      setBusy(true);
      setError('');
      const result = await work();
      if (successMessage) {
        onMessage?.(successMessage);
      }
      return result;
    } catch (err) {
      setError(err.message || 'Gig action failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    await runAction(async () => {
      const wasExisting = !!form.id;
      const savedGig = await persistGig();
      onMessage?.(wasExisting ? 'Gig saved' : 'Gig created');
      return savedGig;
    });
  };

  const handleSendInvite = async (index) => {
    const performer = performers[index];
    if (!String(performer?.displayName || '').trim()) {
      setError('Add Performer name to send invite');
      return;
    }

    await runAction(async () => {
      const savedGig = await persistGig();
      const savedForm = gigToFormState(savedGig);
      setForm(savedForm);
      const targetPerformer = savedForm.performers[index];
      if (!targetPerformer) {
        throw new Error('Save the performer before sending an invite');
      }
      const result = await sendGigInvite(savedGig.id, buildGigInvitePayload(targetPerformer));
      setForm(gigToFormState(result.gig));
      await onChanged?.(result.gig);
      return result;
    }, 'Performer invite sent');
  };

  const handleCopySubmissionLink = async (performer) => {
    const url = buildSubmissionUrl(performer?.submissionToken);
    if (!url) {
      setError('No submission link is available yet for this performer');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        onMessage?.('Submission link copied');
        return;
      }
      throw new Error('Clipboard unavailable');
    } catch (err) {
      setError(err.message || 'Failed to copy submission link');
    }
  };

  const buildSubmissionInviteMessage = (performer) => {
    const url = buildSubmissionUrl(performer?.submissionToken);
    if (!url) return '';

    const performerName = performer?.displayName || 'there';
    const venueLabel = siteName || 'the venue';
    const gigTitle = form?.title || 'this gig';
    const start = parseLocalDateTime(form?.startsAtLocal);
    const end = parseLocalDateTime(form?.endsAtLocal);
    const dateLabel = start
      ? start.toLocaleDateString([], {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : 'the scheduled date';
    const timeLabel = start && end
      ? `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} to ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : 'the scheduled time';
    const setLabel = performer?.setStartTime && performer?.setEndTime
      ? ` Your current set is ${performer.setStartTime} to ${performer.setEndTime}.`
      : '';

    return [
      `Thank you ${performerName} for confirming your availability to play at ${venueLabel} for ${gigTitle} on ${dateLabel} from ${timeLabel}.${setLabel}`,
      'Please click the link below to confirm the booking and add as much information about yourself as possible, including bio, artwork, and all social/music links, so we can promote your channels effectively.',
      url,
    ].join('\n\n');
  };

  const handleCopySubmissionLinkAndMessage = async (performer) => {
    const message = buildSubmissionInviteMessage(performer);
    if (!message) {
      setError('No submission link is available yet for this performer');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        onMessage?.('Submission link and message copied');
        return;
      }
      throw new Error('Clipboard unavailable');
    } catch (err) {
      setError(err.message || 'Failed to copy submission message');
    }
  };

  const getPerformerExpandKey = (performer, index) => performer?.id ? `performer-${performer.id}` : `new-${index}`;

  const isPerformerExpanded = (performer, index) => {
    const key = getPerformerExpandKey(performer, index);
    if (Object.prototype.hasOwnProperty.call(expandedPerformerKeys, key)) {
      return !!expandedPerformerKeys[key];
    }
    return !isExistingGig;
  };

  const togglePerformerExpanded = (performer, index) => {
    const key = getPerformerExpandKey(performer, index);
    setExpandedPerformerKeys((prev) => ({
      ...prev,
      [key]: !isPerformerExpanded(performer, index),
    }));
  };

  const handleDeleteReusableArtist = async (index) => {
    await runAction(async () => {
      const performer = performers[index];
      const artistId = Number(performer?.artistId || 0);
      if (!artistId) {
        throw new Error('No saved artist is linked to this performer');
      }

      await deleteArtist(artistId);

      setArtists((current) => current.filter((artist) => artist.id !== artistId));
      setForm((current) => ({
        ...current,
        performers: (Array.isArray(current.performers) ? current.performers : []).map((entry) =>
          Number(entry?.artistId || 0) === artistId
            ? { ...entry, artistId: null }
            : entry
        ),
      }));

      if (form.id) {
        const refreshedGig = await getGig(form.id);
        setForm(gigToFormState(refreshedGig));
        await onChanged?.(refreshedGig);
      }

      return artistId;
    }, 'Artist deleted');
  };

  const handleHighlightUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (!resolvedSiteId) {
      setError('A site is required before uploading highlight photos');
      return;
    }

    const imageFiles = files.filter((file) => file && String(file.type || '').startsWith('image/'));
    if (!imageFiles.length) {
      setError('Please choose image files for gig highlights');
      return;
    }

    const nextTotal = highlightPhotos.length + imageFiles.length;
    if (nextTotal > MAX_GIG_HIGHLIGHT_PHOTOS) {
      setError(`You can attach up to ${MAX_GIG_HIGHLIGHT_PHOTOS} highlight photos to a gig`);
      return;
    }

    try {
      setUploadingHighlights(true);
      setError('');
      const uploadedItems = await uploadGigHighlightPhotos({
        siteId: resolvedSiteId,
        files: imageFiles,
      });
      updateHighlightPhotos((current) => [
        ...current,
        ...uploadedItems.map((item, index) => ({
          ...item,
          sortOrder: (current.length + index) * 10,
        })),
      ]);
      onMessage?.('Highlight photos added. Save the gig to publish them.');
    } catch (err) {
      setError(err.message || 'Failed to upload highlight photos');
    } finally {
      setUploadingHighlights(false);
    }
  };

  const handleRemoveHighlight = (photoToRemove) => {
    updateHighlightPhotos((current) =>
      current.filter((item) => Number(item.creativeId || 0) !== Number(photoToRemove?.creativeId || 0))
    );
  };

  const handleSelectGigArtwork = (asset) => {
    const assetId = Number(asset?.id || 0);
    if (!assetId) return;

    updateForm({
      gigArtworkCreativeId: String(assetId),
      gigArtworkThumbnailUrl:
        asset?.preview_api_url
        || asset?.thumbnail_api_url
        || `/api/creative/assets/${assetId}/files/preview`,
    });
    setGigArtworkBrowserOpen(false);
  };

  const beginReplacement = (performer) => {
    setReplacementTargetId(performer?.id || null);
    setReplacementDraft({
      ...createBlankPerformer(resolvedSiteId),
      setStartTime: performer?.setStartTime || '',
      setEndTime: performer?.setEndTime || '',
      inviteChannel: performer?.inviteChannel || '',
      inviteTarget: '',
      replacementReason: '',
    });
    setError('');
  };

  const handleReplaceArtist = async () => {
    await runAction(async () => {
      if (!form.id || !replacementTarget?.id) {
        throw new Error('Save the gig before replacing a performer');
      }

      const result = await replaceGigPerformer(
        form.id,
        replacementTarget.id,
        buildGigInvitePayload(replacementDraft)
      );
      setForm(gigToFormState(result.gig));
      setReplacementTargetId(null);
      setReplacementDraft(createBlankPerformer(resolvedSiteId));
      await onChanged?.(result.gig);
      return result;
    }, 'Replacement invite sent');
  };

  const handleRequestChanges = async () => {
    await runAction(async () => {
      if (!form.id) {
        throw new Error('Save the gig before requesting changes');
      }
      const gig = await requestGigChanges(form.id);
      setForm(gigToFormState(gig));
      await onChanged?.(gig);
      return gig;
    }, 'Gig marked for changes');
  };

  const handlePublish = async () => {
    await runAction(async () => {
      if (!form.id) {
        throw new Error('Save the gig before publishing');
      }
      const gig = await publishGig(form.id);
      setForm(gigToFormState(gig));
      await onChanged?.(gig);
      return gig;
    }, 'Gig published');
  };

  const handleCreateCampaign = () => {
    if (!form.id) {
      setError('Save and publish the gig before creating a campaign');
      return;
    }

    openGigCampaignInCreativeStudio(
      buildGigCampaignRequest({
        gig: form,
        siteName,
      })
    );
  };

  const handleCancel = async () => {
    await runAction(async () => {
      if (!form.id) {
        throw new Error('Save the gig before cancelling');
      }
      const gig = await cancelGig(form.id);
      setForm(gigToFormState(gig));
      await onChanged?.(gig);
      return gig;
    }, 'Gig cancelled');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={form.id ? 'Gig Details' : 'New Gig'}
      actions={form.id && (hasPreviousGig || hasNextGig) ? (
        <>
          <button
            type="button"
            onClick={onPreviousGig}
            disabled={!hasPreviousGig}
            className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {'<<'}
          </button>
          <button
            type="button"
            onClick={onNextGig}
            disabled={!hasNextGig}
            className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {'>>'}
          </button>
        </>
      ) : null}
    >
      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center text-slate-300">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading gig details…
        </div>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-[1.25fr,0.75fr]">
          <div className="grid content-start gap-4 self-start">
            <div className="self-start rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setGigDetailsExpanded((current) => !current)}
                  className="flex min-w-0 flex-1 items-start gap-3 text-left"
                >
                  <div className="mt-0.5 rounded-lg bg-white/5 p-1 ring-1 ring-white/10">
                    {gigDetailsExpanded ? <ChevronDown className="h-4 w-4 text-slate-300" /> : <ChevronRight className="h-4 w-4 text-slate-300" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">
                      {isExistingGig ? `Gig Details: ${form.title || 'Untitled Gig'}` : 'Gig Details'}
                    </div>
                    <div className="text-xs text-slate-400">
                      {isExistingGig ? rangeSummary : 'Venue-facing event content and schedule.'}
                    </div>
                  </div>
                </button>
                <Badge className="bg-indigo-500/20 text-indigo-100">
                  {formatGigDisplayStatusLabel(gigDisplayStatus)}
                </Badge>
              </div>

              {gigDetailsExpanded ? (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <label className="grid gap-1 md:col-span-2">
                    <span className="text-xs text-slate-300">Title</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.title}
                      onChange={(event) => updateForm({ title: event.target.value })}
                      placeholder="Sunset Sessions"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Slug</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.slug}
                      onChange={(event) => updateForm({ slug: event.target.value })}
                      placeholder="Optional custom slug"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Timezone</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.timezoneName}
                      onChange={(event) => updateForm({ timezoneName: event.target.value })}
                      placeholder="Asia/Ho_Chi_Minh"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Start</span>
                    <input
                      type="datetime-local"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.startsAtLocal}
                      onChange={(event) => updateGigStart(event.target.value)}
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">End</span>
                    <input
                      type="datetime-local"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.endsAtLocal}
                      onChange={(event) => updateGigEnd(event.target.value)}
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">UTC Offset Snapshot (minutes)</span>
                    <input
                      type="number"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.utcOffsetMinutesSnapshot}
                      onChange={(event) => updateForm({ utcOffsetMinutesSnapshot: event.target.value })}
                    />
                  </label>

                  <label className="grid gap-1 md:col-span-2">
                    <span className="text-xs text-slate-300">Description</span>
                    <textarea
                      rows={4}
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={form.description}
                      onChange={(event) => updateForm({ description: event.target.value })}
                      placeholder="Brief public description for the gig"
                    />
                  </label>

                  <div className="grid gap-3 md:col-span-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-white">Gig Image</div>
                        <div className="text-xs text-slate-400">
                          This image is used first on the website. If empty, the scheduled performer artwork is used instead.
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setGigArtworkBrowserOpen(true)}
                          className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:opacity-60"
                          disabled={!resolvedSiteId}
                        >
                          Choose From Photos
                        </button>
                        <button
                          type="button"
                          onClick={() => updateForm({ gigArtworkCreativeId: '', gigArtworkThumbnailUrl: null })}
                          className="rounded-xl bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10 disabled:opacity-60"
                          disabled={!form.gigArtworkCreativeId}
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>

                    {form.gigArtworkThumbnailUrl ? (
                      <div className="overflow-hidden rounded-2xl bg-black/30 ring-1 ring-white/10">
                        <img
                          src={form.gigArtworkThumbnailUrl}
                          alt={form.title || 'Gig artwork'}
                          className="block h-48 w-full object-contain p-4"
                        />
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-white/10 px-3 py-4 text-sm text-slate-400">
                        No gig image selected yet.
                      </div>
                    )}
                  </div>

                  {isExistingGig ? (
                    <div className="md:col-span-2 rounded-xl bg-black/20 px-3 py-2 text-sm text-slate-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-indigo-200" />
                        <span>{rangeSummary}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="rounded-xl bg-white/5 p-2 ring-1 ring-white/10">
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Performer Lineup</div>
                    <div className="text-xs text-slate-400">Assign set times, invite artists, and preserve replacement history.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addPerformer}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                >
                  <Plus className="h-4 w-4" />
                  Add Performer
                </button>
              </div>

              <div className="mt-4 grid gap-4">
                {activeEntries.map(({ performer, index }, activeIndex) => {
                  const selectedArtist = artistsById.get(Number(performer.artistId));
                  const linkedArtistLabel = selectedArtist?.name || (performer.artistId ? `Artist #${performer.artistId}` : null);
                  const submissionUrl = buildSubmissionUrl(performer.submissionToken);
                  const performerExpanded = isPerformerExpanded(performer, index);
                  const hasPerformerDisplayName = !!String(performer.displayName || '').trim();
                  const artistStorageEligible = performer.storeProfile !== false && String(performer.status || '').toLowerCase() === 'submitted';

                  return (
                    <div key={performer.id || `performer-${index}`} className="rounded-2xl bg-black/20 p-4 ring-1 ring-white/10">
                      <div className="grid gap-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => togglePerformerExpanded(performer, index)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <div className="rounded-lg bg-white/5 p-1 ring-1 ring-white/10">
                              {performerExpanded ? (
                                <ChevronDown className="h-4 w-4 text-slate-300" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-300" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-white">
                                {performerLabel(performer, activeIndex)}
                              </div>
                              <div className="truncate text-xs text-slate-400">
                                {linkedArtistLabel || 'Standalone performer record'}
                              </div>
                            </div>
                          </button>
                          <Badge className="bg-slate-500/20 text-slate-200">
                            {String(performer.status || 'awaiting_submission').replace(/_/g, ' ')}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleSendInvite(index)}
                            disabled={busy || !hasPerformerDisplayName}
                            className="inline-flex items-center gap-2 rounded-xl bg-amber-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                          >
                            <Send className="h-4 w-4" />
                            {hasPerformerDisplayName ? 'Send Invite' : 'Add Performer name to send invite'}
                          </button>
                          {submissionUrl ? (
                            <button
                              type="button"
                              onClick={() => handleCopySubmissionLink(performer)}
                              className="inline-flex items-center gap-2 rounded-xl bg-sky-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-600"
                            >
                              <Copy className="h-4 w-4" />
                              Copy Link
                            </button>
                          ) : null}
                          {selectedArtist ? (
                            <button
                              type="button"
                              onClick={() => handleDeleteReusableArtist(index)}
                              disabled={busy}
                              className="inline-flex items-center gap-2 rounded-xl bg-rose-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          ) : null}
                          {submissionUrl ? (
                            <button
                              type="button"
                              onClick={() => handleCopySubmissionLinkAndMessage(performer)}
                              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-600"
                            >
                              <Copy className="h-4 w-4" />
                              Copy Link and Message
                            </button>
                          ) : null}
                          {form.id && performer.id ? (
                            <button
                              type="button"
                              onClick={() => beginReplacement(performer)}
                              disabled={busy}
                              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:opacity-60"
                            >
                              <RefreshCcw className="h-4 w-4" />
                              Replace Artist
                            </button>
                          ) : null}
                          {!performer.id && activeEntries.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removePerformer(index)}
                              disabled={busy}
                              className="inline-flex items-center gap-2 rounded-xl bg-rose-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {submissionUrl ? (
                        <div className="mt-3 rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-300 ring-1 ring-white/10">
                          Submission link ready for this performer.
                        </div>
                      ) : null}

                      {performerExpanded ? (
                        <>
                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <div className="grid gap-3 md:col-span-2 rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Internal Artist Reuse</div>
                                  <div className="text-xs text-slate-500">
                                    Reuse a saved artist profile. Submitted performers who allow profile storage are saved automatically.
                                  </div>
                                </div>
                                {selectedArtist ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteReusableArtist(index)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-2 rounded-xl bg-rose-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </button>
                                ) : null}
                              </div>

                              {performer.storeProfile === false ? (
                                <div className="rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-100 ring-1 ring-amber-400/20">
                                  This performer has opted out of profile storage, so they cannot be saved to the reusable artist list.
                                </div>
                              ) : artistStorageEligible && !selectedArtist ? (
                                <div className="rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-300 ring-1 ring-white/10">
                                  This performer should be added to the reusable artist list automatically when they submit their booking form.
                                </div>
                              ) : !artistStorageEligible && !selectedArtist ? (
                                <div className="rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-300 ring-1 ring-white/10">
                                  Reusable profiles are created automatically after the performer submits their booking form.
                                </div>
                              ) : null}

                              <label className="grid gap-1">
                                <span className="text-xs text-slate-300">Select Existing Artist</span>
                                <select
                                  className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                  value={performer.artistId || ''}
                                  onChange={(event) => {
                                    const nextArtistId = Number(event.target.value) || null;
                                    if (!nextArtistId) {
                                      updatePerformer(index, { artistId: null });
                                      return;
                                    }
                                    const artist = artists.find((entry) => entry.id === nextArtistId);
                                    if (!artist) return;
                                    updatePerformer(index, {
                                      ...artistToPerformerFormPatch(artist),
                                      artistId: artist.id,
                                    });
                                  }}
                                >
                                  <option value="">
                                    {artistsLoading ? 'Loading artists…' : artists.length ? 'Choose an artist' : 'No artists saved yet'}
                                  </option>
                                  {artists.map((artist) => (
                                    <option key={artist.id} value={artist.id}>
                                      {artist.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Display Name</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.displayName || ''}
                                onChange={(event) => updatePerformer(index, { displayName: event.target.value })}
                                placeholder="DJ Luna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Instagram</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.instagramUrl || ''}
                                onChange={(event) => updatePerformer(index, { instagramUrl: event.target.value })}
                                placeholder="@djluna or instagram.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Facebook</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.facebookUrl || ''}
                                onChange={(event) => updatePerformer(index, { facebookUrl: event.target.value })}
                                placeholder="@djluna or facebook.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">X</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.twitterUrl || ''}
                                onChange={(event) => updatePerformer(index, { twitterUrl: event.target.value })}
                                placeholder="@djluna or x.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Pinterest</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.pinterestUrl || ''}
                                onChange={(event) => updatePerformer(index, { pinterestUrl: event.target.value })}
                                placeholder="@djluna or pinterest.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Apple Music</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.appleMusicUrl || ''}
                                onChange={(event) => updatePerformer(index, { appleMusicUrl: event.target.value })}
                                placeholder="music.apple.com/..."
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Mixcloud</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.mixcloudUrl || ''}
                                onChange={(event) => updatePerformer(index, { mixcloudUrl: event.target.value })}
                                placeholder="@djluna or mixcloud.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">TikTok</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.tiktokUrl || ''}
                                onChange={(event) => updatePerformer(index, { tiktokUrl: event.target.value })}
                                placeholder="@djluna or tiktok.com/@djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Set Start</span>
                              <input
                                type="time"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.setStartTime || ''}
                                onChange={(event) => updatePerformer(index, { setStartTime: event.target.value })}
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Set End</span>
                              <input
                                type="time"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.setEndTime || ''}
                                onChange={(event) => updatePerformer(index, { setEndTime: event.target.value })}
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Invite Channel</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.inviteChannel || ''}
                                onChange={(event) => updatePerformer(index, { inviteChannel: event.target.value })}
                                placeholder="instagram-dm"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Invite Target</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.inviteTarget || ''}
                                onChange={(event) => updatePerformer(index, { inviteTarget: event.target.value })}
                                placeholder="@djluna"
                              />
                            </label>

                            <label className="grid gap-1 md:col-span-2">
                              <span className="text-xs text-slate-300">Bio</span>
                              <textarea
                                rows={3}
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.bio || ''}
                                onChange={(event) => updatePerformer(index, { bio: event.target.value })}
                                placeholder="Short performer bio"
                              />
                            </label>

                            <label className="grid gap-1 md:col-span-2">
                              <span className="text-xs text-slate-300">Genres</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.genresText || ''}
                                onChange={(event) => updatePerformer(index, { genresText: event.target.value })}
                                placeholder="house, disco, afro-house"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">SoundCloud</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.soundcloudUrl || ''}
                                onChange={(event) => updatePerformer(index, { soundcloudUrl: event.target.value })}
                                placeholder="@djluna or soundcloud.com/djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Spotify</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.spotifyUrl || ''}
                                onChange={(event) => updatePerformer(index, { spotifyUrl: event.target.value })}
                                placeholder="open.spotify.com/..."
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">YouTube</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.youtubeUrl || ''}
                                onChange={(event) => updatePerformer(index, { youtubeUrl: event.target.value })}
                                placeholder="@djluna or youtube.com/@djluna"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Contact Email</span>
                              <input
                                type="email"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.contactEmail || ''}
                                onChange={(event) => updatePerformer(index, { contactEmail: event.target.value })}
                                placeholder="booking@example.com"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Phone / WhatsApp</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.contactPhone || ''}
                                onChange={(event) => updatePerformer(index, { contactPhone: event.target.value })}
                                placeholder="+44 7700 900123"
                              />
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Preferred Contact Method</span>
                              <select
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.preferredContactMethod || ''}
                                onChange={(event) => updatePerformer(index, { preferredContactMethod: event.target.value })}
                              >
                                {PREFERRED_CONTACT_METHOD_OPTIONS.map((option) => (
                                  <option key={option.value || 'none'} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Preferred Contact Details</span>
                              <input
                                type="text"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.preferredContactDetails || ''}
                                onChange={(event) => updatePerformer(index, { preferredContactDetails: event.target.value })}
                                placeholder="Best email, number, or DM handle to use first"
                              />
                            </label>

                            <label className="md:col-span-2 flex items-start gap-3 rounded-2xl bg-white/5 px-3 py-3 text-sm text-slate-300 ring-1 ring-white/10">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={performer.storeProfile !== false}
                                onChange={(event) => updatePerformer(index, { storeProfile: event.target.checked })}
                              />
                              <span>
                                Store this performer profile for future gigs and artist reuse. Untick if they do not want their details stored after this booking.
                              </span>
                            </label>

                            <label className="md:col-span-2 flex items-start gap-3 rounded-2xl bg-white/5 px-3 py-3 text-sm text-slate-300 ring-1 ring-white/10">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!performer.allowContactSharing}
                                onChange={(event) => updatePerformer(index, { allowContactSharing: event.target.checked })}
                              />
                              <span>
                                Venue may share this performer&apos;s personal contact details with other event managers and venue owners who ask about booking them.
                              </span>
                            </label>

                            <label className="grid gap-1">
                              <span className="text-xs text-slate-300">Artwork Creative ID</span>
                              <input
                                type="number"
                                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                                value={performer.artworkCreativeId || ''}
                                onChange={(event) => updatePerformer(index, { artworkCreativeId: event.target.value })}
                                placeholder="Optional"
                              />
                            </label>
                          </div>

                          {performer.artworkThumbnailUrl ? (
                            <div className="mt-4 overflow-hidden rounded-2xl bg-black/20 ring-1 ring-white/10">
                              <img
                                src={performer.artworkThumbnailUrl}
                                alt={performer.displayName || 'Performer artwork'}
                                className="h-48 w-full bg-slate-950/80 object-contain p-2"
                              />
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {showHighlightSection ? (
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">Photo Highlights</div>
                    <div className="text-xs text-slate-400">
                      Upload post-event images for the public Previous Events page.
                    </div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20">
                    <Plus className="h-4 w-4" />
                    {uploadingHighlights ? 'Uploading…' : 'Upload Photos'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={uploadingHighlights || busy || highlightPhotos.length >= MAX_GIG_HIGHLIGHT_PHOTOS}
                      onChange={handleHighlightUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                <div className="mt-3 rounded-xl bg-black/20 px-3 py-2 text-xs text-slate-300 ring-1 ring-white/10">
                  {highlightPhotos.length
                    ? `${highlightPhotos.length} of ${MAX_GIG_HIGHLIGHT_PHOTOS} highlight photos attached. Save the gig after edits so they appear publicly.`
                    : `Attach up to ${MAX_GIG_HIGHLIGHT_PHOTOS} highlight photos. Save the gig after uploads so they appear publicly.`}
                </div>

                {gigCompleted && !highlightPhotos.length ? (
                  <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                    This gig is completed. Upload post-event photos here so the public Previous Events section has images to use.
                  </div>
                ) : null}

                {highlightPhotos.length ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {highlightPhotos.map((photo, index) => (
                      <div key={photo.id || `${photo.creativeId}-${index}`} className="overflow-hidden rounded-2xl bg-black/20 ring-1 ring-white/10">
                        {photo.previewUrl ? (
                          <img
                            src={photo.previewUrl}
                            alt={`Gig highlight ${index + 1}`}
                            className="h-40 w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-40 items-center justify-center text-sm text-slate-400">
                            Preview unavailable
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-3 px-3 py-2">
                          <div className="text-xs text-slate-400">
                            Photo {index + 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveHighlight(photo)}
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600/90 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-black/20 px-4 py-8 text-center text-sm text-slate-400">
                    No highlight photos attached yet.
                  </div>
                )}
              </div>
            ) : null}

            {replacementTarget ? (
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">Replace Artist</div>
                    <div className="text-xs text-slate-400">
                      {`Swap out ${replacementTarget.displayName || 'this performer'} and keep the original row in history.`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setReplacementTargetId(null);
                      setReplacementDraft(createBlankPerformer(resolvedSiteId));
                    }}
                    className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                  >
                    Close
                  </button>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <label className="grid gap-1 md:col-span-2">
                    <span className="text-xs text-slate-300">Select Existing Artist</span>
                    <select
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.artistId || ''}
                      onChange={(event) => {
                        const nextArtistId = Number(event.target.value) || null;
                        if (!nextArtistId) {
                          setReplacementDraft((current) => ({ ...current, artistId: null }));
                          return;
                        }
                        const artist = artists.find((entry) => entry.id === nextArtistId);
                        if (!artist) return;
                        setReplacementDraft((current) => ({
                          ...current,
                          ...artistToPerformerFormPatch(artist),
                          artistId: artist.id,
                        }));
                      }}
                    >
                      <option value="">
                        {artistsLoading ? 'Loading artists…' : artists.length ? 'Choose an artist' : 'No artists saved yet'}
                      </option>
                      {artists.map((artist) => (
                        <option key={artist.id} value={artist.id}>
                          {artist.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Display Name</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.displayName || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, displayName: event.target.value }))}
                      placeholder="DJ Nova"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Instagram</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.instagramUrl || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, instagramUrl: event.target.value }))}
                      placeholder="@djreplacement or instagram.com/djreplacement"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Set Start</span>
                    <input
                      type="time"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.setStartTime || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, setStartTime: event.target.value }))}
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Set End</span>
                    <input
                      type="time"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.setEndTime || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, setEndTime: event.target.value }))}
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Invite Channel</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.inviteChannel || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, inviteChannel: event.target.value }))}
                      placeholder="instagram-dm"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs text-slate-300">Invite Target</span>
                    <input
                      type="text"
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.inviteTarget || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, inviteTarget: event.target.value }))}
                      placeholder="@djreplacement"
                    />
                  </label>

                  <label className="grid gap-1 md:col-span-2">
                    <span className="text-xs text-slate-300">Replacement Reason</span>
                    <textarea
                      rows={2}
                      className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                      value={replacementDraft.replacementReason || ''}
                      onChange={(event) => setReplacementDraft((current) => ({ ...current, replacementReason: event.target.value }))}
                      placeholder="Original artist unavailable"
                    />
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleReplaceArtist}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-600/90 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                  >
                    <Send className="h-4 w-4" />
                    Send Replacement Invite
                  </button>
                  <div className="text-xs text-slate-500">
                    The original performer stays in history as replaced.
                  </div>
                </div>
              </div>
            ) : null}

            {historyEntries.length ? (
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="text-sm font-semibold text-white">Performer History</div>
                <div className="mt-3 grid gap-2">
                  {historyEntries.map(({ performer, index }) => (
                    <div key={performer.id || `history-${index}`} className="rounded-xl bg-black/20 px-3 py-2 ring-1 ring-white/10">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm text-white">{performerLabel(performer, index)}</div>
                        <Badge className="bg-white/10 text-slate-200">
                          {String(performer.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        {performer.replacedAt
                          ? `Replaced ${new Date(performer.replacedAt).toLocaleString()}`
                          : performer.cancelledAt
                            ? `Cancelled ${new Date(performer.cancelledAt).toLocaleString()}`
                            : 'Historical performer row'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid gap-4">
            <GigReviewPanel
              gig={form}
              performers={performers}
              artistsById={artistsById}
              busy={busy}
              onSave={handleSave}
              onRequestChanges={handleRequestChanges}
              onPublish={handlePublish}
              onCancel={handleCancel}
              onCreateCampaign={handleCreateCampaign}
            />

            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
              <div className="flex items-center gap-2">
                <div className="rounded-xl bg-white/5 p-2 ring-1 ring-white/10">
                  <Music2 className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Status Notes</div>
                  <div className="text-xs text-slate-400">Current workflow context for lineup management.</div>
                </div>
              </div>

              <div className="mt-4 space-y-2 text-sm text-slate-300">
                <p><span className="font-semibold text-white">Draft</span> gigs can be edited freely before artist outreach.</p>
                <p><span className="font-semibold text-white">Awaiting Submission</span> means one or more active performers still owe materials.</p>
                <p><span className="font-semibold text-white">Submitted</span> means active performers have responded and are ready for manager review.</p>
                <p><span className="font-semibold text-white">Completed</span> means a published gig has ended and should usually have highlight photos uploaded.</p>
                <p><span className="font-semibold text-white">Replaced</span> performer rows stay visible in history for auditability.</p>
              </div>
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-700/50 bg-rose-900/20 px-3 py-2 text-sm text-rose-200">
                {error}
              </div>
            ) : null}
          </div>
        </div>
      )}
      {gigArtworkBrowserOpen ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 p-4"
          onClick={() => setGigArtworkBrowserOpen(false)}
        >
          <div
            className="max-h-[88vh] w-full max-w-6xl overflow-auto rounded-3xl bg-slate-900 p-4 ring-1 ring-white/10"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-white">Choose Gig Image</div>
                <div className="text-sm text-slate-400">
                  Pick an image for this gig. Website listings will fall back to performer artwork if this is left blank.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setGigArtworkBrowserOpen(false)}
                className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
              >
                Close
              </button>
            </div>

            <PhotoAssetBrowser
              assets={[]}
              siteId={resolvedSiteId}
              selectedAssetId={Number(form.gigArtworkCreativeId || 0) || null}
              onSelectAsset={handleSelectGigArtwork}
              useFolders
            />
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
